import {
  Edit3,
  ExternalLink,
  Gamepad2,
  Heart,
  Home,
  Joystick,
  Loader2,
  Maximize2,
  MessageSquare,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Star,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import { api } from "@/api";
import gamerBackdropUrl from "@/assets/gamer-zone-backdrop-milky-wayne.jpg";
import type { Game, GameWrite, Member } from "@/types";
import "./gamer-zone.css";

const blankForm: GameWrite = {
  title: "",
  description: "",
  launch_url: "",
  repo_url: "",
  thumbnail_url: "",
  builder_name: "Milky Wayne",
  builder_team: "AI Lab",
  status: "live",
  tags: ["Arcade", "AI Lab"],
  controls: ["Keyboard", "Mouse"],
  notes: "",
  is_featured: false,
};

function splitList(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function joinList(value: string[]): string {
  return value.join(", ");
}

function mergeGame(games: Game[], next: Game): Game[] {
  const exists = games.some((game) => game.id === next.id);
  if (!exists) {
    return [next, ...games];
  }
  return games.map((game) => (game.id === next.id ? next : game));
}

function messageFromError(caught: unknown): string {
  if (caught instanceof Error) {
    return caught.message;
  }
  return "Arcade save failed. Check the API server.";
}

interface GamerZonePageProps {
  onBack: () => void;
}

export function GamerZonePage({ onBack }: GamerZonePageProps) {
  const toastTimer = useRef<number | undefined>(undefined);
  const [member, setMember] = useState<Member | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const [me, list] = await Promise.all([api.me(), api.games()]);
        if (cancelled) return;
        setMember(me);
        setGames(list);
        setSelectedGameId((current) => current ?? list[0]?.id ?? null);
        setError(null);
      } catch (caught) {
        if (cancelled) return;
        setError(caught instanceof Error ? caught.message : "Could not load Gamer Zone data.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
      window.clearTimeout(toastTimer.current);
    };
  }, []);

  function showToast(message: string) {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  }

  const selectedGame = useMemo(() => {
    return games.find((game) => game.id === selectedGameId) ?? games[0] ?? null;
  }, [games, selectedGameId]);

  const visibleGames = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return games;
    return games.filter((game) => {
      return [
        game.title,
        game.description,
        game.builder_team,
        game.builder_name,
        game.status,
        game.tags.join(" "),
      ]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [games, query]);

  async function refreshGame(next: Promise<Game>, success: string) {
    try {
      const updated = await next;
      setGames((current) => mergeGame(current, updated));
      setSelectedGameId(updated.id);
      showToast(success);
    } catch (caught) {
      showToast(messageFromError(caught));
      throw caught;
    }
  }

  return (
    <>
      <main
        className="gamer-zone"
        style={{ "--gamer-backdrop": `url(${gamerBackdropUrl})` } as CSSProperties}
      >
        <section className="gamer-hero">
          <button className="gamer-zone-back-link" onClick={onBack}>
            <Home size={15} /> Back to work...
          </button>
          <div className="retro-marquee" aria-label="Gamer Zone marquee">
            <span>*** WELCOME TO THE NBS AI LAB GAMER ZONE *** MILKY WAYNE IS ONLINE ***</span>
          </div>
          <div className="gamer-logo-row">
            <div>
              <div className="under-construction">UNDER CONSTRUCTION FOREVER</div>
              <h1>
                <span>AI LAB</span> GAMER ZONE
              </h1>
              <p>Call yourself a gamer?!? Show your stuff in the NBS AI Lab's GAMER ZONE!</p>
              <p>Climb the leaderboards of our current offerings, or build your own!</p>
            </div>
            <div className="gamer-stats">
              <span>
                <b>{games.length}</b> games
              </span>
              <span>
                <b>{games.reduce((sum, game) => sum + game.favorite_count, 0)}</b> favorites
              </span>
              <span>
                <b>{member?.is_admin ? "Admin" : "Member"}</b> access
              </span>
              <span>
                <b>000042</b> visitors
              </span>
            </div>
          </div>
          <div className="arcade-kicker">
            <Zap size={16} /> Submit the form at the bottom to add your game to our growing roster!
          </div>
        </section>

        <section className="gamer-console">
          <div className="gamer-zone-game-library">
            <div className="library-head">
              <div>
                <h2>Games</h2>
              </div>
              <div className="neon-search">
                <Search size={15} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search games"
                  aria-label="Search games"
                />
              </div>
            </div>

            {loading && <LoadingState />}
            {error && <ErrorState message={error} />}
            {!loading && !error && visibleGames.length === 0 && (
              <div className="empty-zone">
                <Gamepad2 size={32} />
                <p>No games match that signal.</p>
              </div>
            )}

            <div className="game-grid">
              {visibleGames.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  selected={selectedGame?.id === game.id}
                  onSelect={() => setSelectedGameId(game.id)}
                />
              ))}
            </div>
          </div>

          <div className="play-column">
            {selectedGame ? (
              <GamePlayer
                game={selectedGame}
                onRate={(score) =>
                  refreshGame(api.rateGame(selectedGame.id, score), "Rating saved")
                }
                onFavorite={() =>
                  refreshGame(
                    api.favoriteGame(selectedGame.id, !selectedGame.is_favorited),
                    selectedGame.is_favorited ? "Removed" : "Favorited",
                  )
                }
                onComment={(body) =>
                  refreshGame(api.commentGame(selectedGame.id, body), "Comment posted")
                }
              />
            ) : (
              <div className="empty-zone tall">
                <Joystick size={40} />
                <p>Pick a game to boot it up.</p>
              </div>
            )}
          </div>
        </section>

        {member?.is_admin && (
          <AdminGamePanel
            games={games}
            onCreate={(payload) => refreshGame(api.createGame(payload), "Game added")}
            onUpdate={(id, payload) => refreshGame(api.updateGame(id, payload), "Game updated")}
            onDelete={async (id) => {
              try {
                await api.deleteGame(id);
                setGames((current) => current.filter((game) => game.id !== id));
                setSelectedGameId((current) => (current === id ? null : current));
                showToast("Game removed");
              } catch (caught) {
                showToast(messageFromError(caught));
                throw caught;
              }
            }}
          />
        )}
      </main>
      {toast && <div className="gamer-toast">{toast}</div>}
    </>
  );
}

export function GamerZoneUnlockTransition() {
  return (
    <div className="gamer-zone-unlock-transition rocket-launch" role="status" aria-live="polite">
      <div className="launch-stars" />
      <div className="launch-pad" aria-hidden="true">
        <div className="launch-tower">
          <span />
          <span />
          <span />
        </div>
        <div className="rocket-ship">
          <div className="rocket-nose" />
          <div className="rocket-body">
            <span className="rocket-window" />
            <span className="rocket-badge">AI</span>
          </div>
          <div className="rocket-fin fin-left" />
          <div className="rocket-fin fin-right" />
          <div className="rocket-engine" />
          <div className="rocket-plume">
            <span />
            <span />
            <span />
          </div>
        </div>
        <div className="smoke-cloud cloud-a" />
        <div className="smoke-cloud cloud-b" />
        <div className="smoke-cloud cloud-c" />
      </div>
      <div className="unlock-copy">
        <span>SECRET CODE ACCEPTED</span>
        <b>PREPARE FOR LAUNCH !!!</b>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="loading-zone">
      <Loader2 className="gamer-zone-spin" size={22} />
      Loading arcade index
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="error-zone">
      <X size={20} />
      <span>{message}</span>
    </div>
  );
}

function GameCard({
  game,
  selected,
  onSelect,
}: {
  game: Game;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      className={selected ? "gamer-zone-game-card selected" : "gamer-zone-game-card"}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <div
        className="game-thumb"
        style={game.thumbnail_url ? { backgroundImage: `url(${game.thumbnail_url})` } : undefined}
      >
        {!game.thumbnail_url && <Gamepad2 size={30} />}
        {game.is_featured && <span className="featured-chip">Featured</span>}
      </div>
      <div className="gamer-zone-game-card-body">
        <div className="gamer-zone-game-card-top">
          <h3>{game.title}</h3>
          <span>{game.status}</span>
        </div>
        <p>{game.description}</p>
        <div className="gamer-zone-game-card-foot">
          <span>
            <Star size={13} /> {game.rating_count > 0 ? game.average_rating : "New"}
          </span>
          <span>
            <Heart size={13} /> {game.favorite_count}
          </span>
          <span>
            <MessageSquare size={13} /> {game.comment_count}
          </span>
        </div>
      </div>
    </button>
  );
}

function GamePlayer({
  game,
  onRate,
  onFavorite,
  onComment,
}: {
  game: Game;
  onRate: (score: number) => Promise<void>;
  onFavorite: () => Promise<void>;
  onComment: (body: string) => Promise<void>;
}) {
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [favoriting, setFavoriting] = useState(false);

  useEffect(() => {
    setFrameLoaded(false);
    setComment("");
  }, [game.id]);

  async function submitComment(event: FormEvent) {
    event.preventDefault();
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await onComment(comment.trim());
      setComment("");
    } catch {
      // error already surfaced via the toast in refreshGame
    } finally {
      setBusy(false);
    }
  }

  async function handleFavorite() {
    setFavoriting(true);
    try {
      await onFavorite();
    } catch {
      // error already surfaced via the toast in refreshGame
    } finally {
      setFavoriting(false);
    }
  }

  return (
    <article className="gamer-zone-player-panel">
      <header className="gamer-zone-player-head">
        <div>
          <span className="console-label">-- NOW PLAYING --</span>
          <h2>{game.title}</h2>
          <p>By: {game.builder_name}</p>
        </div>
        <div className="player-actions">
          <button
            className={game.is_favorited ? "gamer-zone-icon-btn active" : "gamer-zone-icon-btn"}
            onClick={handleFavorite}
            disabled={favoriting}
            aria-label="Favorite game"
          >
            <Heart size={18} />
          </button>
          <a
            className="gamer-zone-icon-btn"
            href={game.launch_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open game in new tab"
          >
            <ExternalLink size={18} />
          </a>
        </div>
      </header>

      <div className="frame-shell">
        {!frameLoaded && (
          <div className="frame-loading">
            <Loader2 className="gamer-zone-spin" size={24} />
            Booting frame
          </div>
        )}
        <iframe
          key={game.id}
          src={game.launch_url}
          title={`${game.title} game frame`}
          sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock allow-popups"
          onLoad={() => setFrameLoaded(true)}
        />
        <div className="frame-fallback">
          <span>Select "Open Game" for Fullscreen</span>
          <a
            className="gamer-zone-btn btn-neon"
            href={game.launch_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Maximize2 size={16} /> Open Game
          </a>
        </div>
      </div>

      <div className="game-meta-strip">
        {game.tags.map((tag) => (
          <span key={tag}>{tag}</span>
        ))}
      </div>

      <div className="engagement-grid">
        <section className="rating-panel">
          <span className="console-label">Player rating</span>
          <StarRating value={game.current_user_rating ?? 0} onRate={onRate} />
          <p>
            {game.rating_count > 0
              ? `${game.average_rating} average from ${game.rating_count}`
              : "No ratings yet"}
          </p>
        </section>

        <section className="controls-panel">
          <span className="console-label">Controls</span>
          <div className="control-tags">
            {game.controls.map((control) => (
              <span key={control}>{control}</span>
            ))}
          </div>
          {game.repo_url && (
            <a href={game.repo_url} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={14} /> Repo
            </a>
          )}
        </section>
      </div>

      <section className="comments-panel">
        <div className="gamer-zone-comments-head">
          <span className="console-label">Comms channel</span>
          <b>{game.comments.length}</b>
        </div>
        <form onSubmit={submitComment} className="comment-form">
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder="Drop a line!"
            aria-label="Add a playtest note"
          />
          <button className="gamer-zone-btn btn-neon" disabled={!comment.trim() || busy}>
            <MessageSquare size={16} /> Comment
          </button>
        </form>
        <div className="comment-list">
          {game.comments.length === 0 && <p className="gamer-zone-muted">No playtest notes yet.</p>}
          {game.comments.map((item) => (
            <div className="game-comment" key={item.id}>
              <span className="mini-avatar">{item.author.initials}</span>
              <div>
                <b>
                  {item.author.name}
                  {item.author.is_admin && <ShieldCheck size={13} />}
                </b>
                <p>{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </article>
  );
}

function StarRating({
  value,
  onRate,
}: {
  value: number;
  onRate: (score: number) => Promise<void>;
}) {
  const [busyScore, setBusyScore] = useState<number | null>(null);
  const [hoverScore, setHoverScore] = useState<number | null>(null);
  const previewValue = hoverScore ?? value;

  async function rate(score: number) {
    setBusyScore(score);
    try {
      await onRate(score);
    } catch {
      // error already surfaced via the toast in refreshGame
    } finally {
      setBusyScore(null);
    }
  }

  return (
    <div
      className="gamer-zone-star-rating"
      aria-label="Rate game"
      onMouseLeave={() => setHoverScore(null)}
    >
      {[1, 2, 3, 4, 5].map((score) => (
        <button
          key={score}
          className={
            score <= previewValue
              ? hoverScore
                ? "gamer-zone-star preview"
                : "gamer-zone-star filled"
              : "gamer-zone-star"
          }
          onClick={() => rate(score)}
          onFocus={() => setHoverScore(score)}
          onMouseEnter={() => setHoverScore(score)}
          onBlur={() => setHoverScore(null)}
          disabled={busyScore !== null}
          aria-pressed={score <= value}
          aria-label={`Rate ${score} stars`}
        >
          {busyScore === score ? (
            <Loader2 className="gamer-zone-spin" size={20} />
          ) : (
            <Star size={20} />
          )}
        </button>
      ))}
    </div>
  );
}

function AdminGamePanel({
  games,
  onCreate,
  onUpdate,
  onDelete,
}: {
  games: Game[];
  onCreate: (payload: GameWrite) => Promise<void>;
  onUpdate: (id: number, payload: GameWrite) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<GameWrite>(blankForm);
  const [tagText, setTagText] = useState(joinList(blankForm.tags));
  const [controlText, setControlText] = useState(joinList(blankForm.controls));
  const [busy, setBusy] = useState(false);

  function startEdit(game: Game) {
    setEditingId(game.id);
    setForm({
      title: game.title,
      description: game.description,
      launch_url: game.launch_url,
      repo_url: game.repo_url ?? "",
      thumbnail_url: game.thumbnail_url ?? "",
      builder_name: game.builder_name,
      builder_team: game.builder_team,
      status: game.status,
      tags: game.tags,
      controls: game.controls,
      notes: game.notes,
      is_featured: game.is_featured,
    });
    setTagText(joinList(game.tags));
    setControlText(joinList(game.controls));
  }

  function reset() {
    setEditingId(null);
    setForm(blankForm);
    setTagText(joinList(blankForm.tags));
    setControlText(joinList(blankForm.controls));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const payload: GameWrite = {
      ...form,
      repo_url: form.repo_url || null,
      thumbnail_url: form.thumbnail_url || null,
      tags: splitList(tagText),
      controls: splitList(controlText),
    };
    setBusy(true);
    try {
      if (editingId) {
        await onUpdate(editingId, payload);
      } else {
        await onCreate(payload);
      }
      reset();
    } catch {
      // error already surfaced via the toast in refreshGame
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="gamer-zone-admin-panel">
      <div className="gamer-zone-admin-head">
        <div>
          <span className="console-label">DEVELOPER ZONE</span>
          <h2>SUBMIT A GAME</h2>
        </div>
        <button className="gamer-zone-btn btn-ghost dark" onClick={reset}>
          <Plus size={16} /> New game
        </button>
      </div>

      <div className="admin-layout">
        <form className="admin-form" onSubmit={submit}>
          <label>
            Title
            <input
              required
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({ ...current, title: event.target.value }))
              }
            />
          </label>
          <label>
            Launch URL
            <input
              required
              type="url"
              value={form.launch_url}
              onChange={(event) =>
                setForm((current) => ({ ...current, launch_url: event.target.value }))
              }
            />
          </label>
          <label className="wide">
            Description
            <textarea
              required
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({ ...current, description: event.target.value }))
              }
            />
          </label>
          <label>
            Repo URL
            <input
              type="url"
              value={form.repo_url ?? ""}
              onChange={(event) =>
                setForm((current) => ({ ...current, repo_url: event.target.value }))
              }
            />
          </label>
          <label>
            Thumbnail URL
            <input
              type="url"
              value={form.thumbnail_url ?? ""}
              onChange={(event) =>
                setForm((current) => ({ ...current, thumbnail_url: event.target.value }))
              }
            />
          </label>
          <label>
            Builder
            <input
              required
              value={form.builder_name}
              onChange={(event) =>
                setForm((current) => ({ ...current, builder_name: event.target.value }))
              }
            />
          </label>
          <label>
            Team
            <input
              required
              value={form.builder_team}
              onChange={(event) =>
                setForm((current) => ({ ...current, builder_team: event.target.value }))
              }
            />
          </label>
          <label>
            Status
            <input
              required
              value={form.status}
              onChange={(event) =>
                setForm((current) => ({ ...current, status: event.target.value }))
              }
            />
          </label>
          <label>
            Tags
            <input value={tagText} onChange={(event) => setTagText(event.target.value)} />
          </label>
          <label>
            Controls
            <input value={controlText} onChange={(event) => setControlText(event.target.value)} />
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.is_featured}
              onChange={(event) =>
                setForm((current) => ({ ...current, is_featured: event.target.checked }))
              }
            />
            Featured
          </label>
          <label className="wide">
            Notes
            <textarea
              value={form.notes}
              onChange={(event) =>
                setForm((current) => ({ ...current, notes: event.target.value }))
              }
            />
          </label>
          <div className="form-actions wide">
            <button className="gamer-zone-btn btn-ghost dark" type="button" onClick={reset}>
              <X size={16} /> Clear
            </button>
            <button className="gamer-zone-btn btn-neon" disabled={busy}>
              {busy ? <Loader2 className="gamer-zone-spin" size={16} /> : <Save size={16} />}
              {editingId ? "Save game" : "Add game"}
            </button>
          </div>
        </form>

        <div className="admin-list">
          {games.map((game) => (
            <div className="gamer-zone-admin-row" key={game.id}>
              <div>
                <b>{game.title}</b>
                <span>
                  {game.status} by {game.builder_team}
                </span>
              </div>
              <button
                className="gamer-zone-icon-btn"
                onClick={() => startEdit(game)}
                aria-label={`Edit ${game.title}`}
              >
                <Edit3 size={16} />
              </button>
              <button
                className="gamer-zone-icon-btn danger"
                onClick={() => {
                  if (window.confirm(`Remove ${game.title}?`)) {
                    // [STUB] replace with design-system confirm dialog
                    void onDelete(game.id).catch(() => {
                      // toast already shown by the parent handler
                    });
                  }
                }}
                aria-label={`Remove ${game.title}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
